import React, { useEffect } from 'react';
import moment from 'moment';
import { useTable, usePagination, type Column } from 'react-table';
import { Link } from 'react-router-dom';
import { Box, Button, Flex, Spinner, Stack, Text } from '@chakra-ui/react';
import TableBox from '../../common/TableBox';
import ReactTablePagination from '../../../common/ReactTablePagination';
import EmptyState from '../../../common/EmptyState';
import MessageBatchMoreInfo from './MessageBatchMoreInfo';
import MessageHistoryRow from './MessageHistoryRow';
import MessageHistoryHeader from './MessageHistoryHeader';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';
import LockedActionButton from '../../common/LockedActionButton';
import { getSafeHttpUrl } from '../../../utils/getSafeHttpUrl';
import { ERROR_STATUSES, LOADING_STATUSES } from './messagingHistory.constants';
import type {
  MessageHistoryProgress,
  MessageHistoryStatus,
  MessagingHistoryData,
  MessagingHistoryListProps,
  MessagingHistoryTableInstance,
} from './messagingHistory.types';

const getDisplayStatus = (status: string): MessageHistoryStatus => {
  if (status === 'failed') return 'failed';
  if (status === 'cancelled') return 'cancelled';
  if (status === 'completedWithErrors') return 'completedWithErrors';
  if (status === 'partially_cancelled') return 'partiallyCancelled';
  if (status === 'cancellation_failed') return 'cancellationFailed';
  if (status === 'completed' || status === 'successful') return 'successful';
  if (status === 'scheduled') return 'scheduled';
  if (status === 'validating' || status === 'preparing') return 'preparing';
  return 'processing';
};

const getLoadingMessage = (
  status: string,
  progress?: MessageHistoryProgress | null
): string => {
  if (status === 'validating' || status === 'preparing') {
    return 'Making sure everything looks right...';
  }

  if (progress) {
    return "We're working hard behind the scenes...";
  }

  return "We're working on it... this may take a few minutes";
};

const getErrorMessage = (status: string): { title: string; detail: string } => {
  const reportAction = 'Download the error report, fix and resend';

  switch (status) {
    case 'failed':
      return {
        title: 'Errors found, batch cancelled.',
        detail: reportAction,
      };
    case 'completedWithErrors':
      return {
        title: 'Completed with some errors.',
        detail: reportAction,
      };
    case 'partially_cancelled':
      return {
        title: 'Some messages could not be cancelled.',
        detail: 'These messages will still be sent.',
      };
    case 'cancellation_failed':
      return {
        title: 'Cancellation failed.',
        detail: 'The batch will still be sent as scheduled.',
      };
    default:
      return { title: 'An error occurred.', detail: '' };
  }
};

const MessagingHistoryList = ({
  columns,
  heading,
  data,
  fetchData,
  isLoading,
  pageCount,
  total,
  cancel,
  tableType,
}: MessagingHistoryListProps) => {
  const isReadOnly = useIsReadOnly();
  const tableInstance = useTable<MessagingHistoryData>(
    {
      columns: columns as Column<MessagingHistoryData>[],
      data,
      manualPagination: true,
      pageCount,
      initialState: { pageIndex: 0 },
    },
    usePagination
  ) as MessagingHistoryTableInstance;
  const {
    prepareRow,
    page,
    state: { pageIndex, pageSize },
  } = tableInstance;

  useEffect(() => {
    fetchData({ pageIndex, pageSize });
  }, [fetchData, pageIndex, pageSize]);

  if (isLoading) {
    return (
      <Box textAlign="center">
        <Spinner />
      </Box>
    );
  }

  if (data.length === 0 && !isLoading) {
    return (
      <EmptyState
        heading={
          tableType === 'scheduled'
            ? 'No Scheduled Messages'
            : 'No Messaging History'
        }
        text={
          tableType === 'scheduled'
            ? 'Click on the button below to start scheduling your messages.'
            : 'Click on the button below to start sending your messages.'
        }
      >
        {isReadOnly ? (
          <LockedActionButton reason="send-messages" size="sm">
            New Message
          </LockedActionButton>
        ) : (
          <Button size="sm" as={Link} to="/messaging/create">
            New Message
          </Button>
        )}
      </EmptyState>
    );
  }

  return (
    <TableBox
      borderRadius="10px"
      display="flex"
      flexDirection="column"
      gap="10px"
      padding="28px 40px"
    >
      <MessageHistoryHeader
        title={heading}
        total={total}
        dateLabel="Send time"
      />
      <Stack spacing="10px">
        {page.map((row) => {
          prepareRow(row);
          const message = row.original;
          const moreInfoCell = row.cells.find(
            (cell) => cell.column.id === 'moreInfo'
          ) || { row };
          const cancelDisabled =
            isReadOnly ||
            !!(
              message.scheduledAtRaw &&
              moment(message.scheduledAtRaw).isBefore(
                moment().add(30, 'minutes')
              )
            );
          const isErrorRow = ERROR_STATUSES.has(message.status);
          const isCancelledRow = message.status === 'cancelled';
          const isLoadingRow = LOADING_STATUSES.has(message.status);
          const displayDate = message.sendTime || '';
          const commonProps = {
            name: message.name,
            content: message.content,
            recipients: message.recipients ?? 0,
            status: getDisplayStatus(message.status),
            date: displayDate,
          };

          const openErrorReport = () => {
            const reportUrl = getSafeHttpUrl(
              message.errorsCsv,
              window.location.origin
            );

            if (reportUrl) {
              window.open(reportUrl, '_blank', 'noopener,noreferrer');
              return;
            }
          };

          return (
            <MessageBatchMoreInfo
              key={row.id}
              cell={moreInfoCell}
              cancel={cancel}
              fetchData={fetchData}
              pageIndex={pageIndex}
              pageSize={pageSize}
              cancelDisabled={cancelDisabled}
              customTriggerButtonComponent={(openMoreInfo) => {
                if (isErrorRow && !isCancelledRow) {
                  const errorMsg = getErrorMessage(message.status);
                  const showReportAction =
                    message.status === 'failed' ||
                    message.status === 'completedWithErrors';

                  return (
                    <MessageHistoryRow
                      {...commonProps}
                      variant="error"
                      message={
                        <>
                          <Text as="span" fontWeight="700">
                            {errorMsg.title}
                          </Text>{' '}
                          {errorMsg.detail}
                        </>
                      }
                      {...(showReportAction
                        ? { onAction: openErrorReport }
                        : {})}
                      onClick={openMoreInfo}
                    />
                  );
                }

                if (isLoadingRow) {
                  return (
                    <MessageHistoryRow
                      {...commonProps}
                      variant="loading"
                      message={getLoadingMessage(
                        message.status,
                        message.progress
                      )}
                      progress={message.progress || undefined}
                      onClick={openMoreInfo}
                    />
                  );
                }

                return (
                  <MessageHistoryRow
                    {...commonProps}
                    variant="standard"
                    onClick={openMoreInfo}
                  />
                );
              }}
            />
          );
        })}
      </Stack>
      {data.length > 0 && (
        <Flex
          mt={6}
          flexDirection={{ base: 'column-reverse', '2xl': 'row' }}
          justifyContent={{ base: 'none', '2xl': 'flex-end' }}
        >
          <ReactTablePagination
            {...tableInstance}
            pageIndex={pageIndex}
            pageSize={pageSize}
          />
        </Flex>
      )}
    </TableBox>
  );
};

export default MessagingHistoryList;
